import { createSelector } from '@ngrx/store';
import {
  AllBookedProductsTotalPrice,
  BookedProducts,
  BookingProductType,
  BookingTariff,
  BookingTariffType,
  isBookingProductTypePackage,
  isBookingProductTypeTariff,
  SharedBookedPersonIdTariffsTotalCount
} from '@products/models/booking.model';
import { RedeemedPromoCode } from '@products/models/smart-promotions';
import {
  getBookedPackageTariffs,
  getBookedPackageTariffTypesByTicketTypeId
} from '@store/products/booking/product/package/package-booking.selectors';
import {
  getBookedTariffs,
  getBookedTariffTypeByTicketTypeId
} from '@store/products/booking/product/tariff/tariff-booking.selectors';
import { selectRedeemedValidDiscountPromoCodes } from '@store/products/booking/smart-promotion/booking-smart-promotion.selectors';
import { getProductBookingState } from '@store/products/product.selectors';
import { isEmpty } from 'lodash';

/**
 * Get booked products list
 * @description Lists all booked products
 * @returns Booking or empty array
 */
export const getBookedProducts = createSelector(
  getProductBookingState,
  (productBookingState): BookedProducts => productBookingState.list
);

/**
 * Get all booked tariffs
 * @description Get all tariffs from bookings
 * @returns Booked tariff or empty array
 */
export const getAllBookedTariffs = createSelector(
  getBookedTariffs,
  getBookedPackageTariffs,
  (bookedTariffs: BookingTariff[], bookedPackageTariffs: BookingTariff[]): BookingTariff[] => {
    return [...bookedTariffs, ...bookedPackageTariffs];
  }
);

/**
 * Get all booked tariffs
 * @description Get all cart tariffs by ticket person id
 * @param ticketTypeId
 * @param ticketPersonId
 * @returns Booked tariffs or empty array
 */
export const getAllBookedTariffsByTicketPersonId = (ticketPersonId: number) =>
  createSelector(getAllBookedTariffs, (allBookedTariffs: BookingTariff[]): BookingTariff[] => {
    const bookedTariffs: BookingTariff[] = [];

    allBookedTariffs.forEach((bookedTariff) => {
      if (bookedTariff.ticketPersonId === ticketPersonId) {
        bookedTariffs.push(bookedTariff);
      }
    });

    return bookedTariffs;
  });

/**
 * Get booked tariff
 * @description Get booked tariff
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Booked tariff or null
 */
export const getBookedTariff = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getAllBookedTariffs,
    (bookedTariffs: BookingTariff[]): BookingTariff =>
      bookedTariffs.find(
        (bookedTariff) =>
          bookedTariff.ticketTypeId === ticketTypeId &&
          bookedTariff.ticketPersonId === ticketPersonId &&
          bookedTariff.voucherCode === voucherCode &&
          bookedTariff.packageNumber === packageNumber &&
          bookedTariff.packageIndex === packageIndex
      )
  );

/**
 * Get booked tariff count
 * @description Get number of booked tariffs
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Number
 */
export const getBookedTariffCount = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariff(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (bookedTariff: BookingTariff): number => (bookedTariff ? bookedTariff.count : 0)
  );

/**
 * Get booked tariff price
 * @description Get price of booked tariff
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Number
 */
export const getBookedTariffPrice = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariff(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (bookedTariff: BookingTariff): number =>
      bookedTariff ? (!isEmpty(bookedTariff.promoCodeDiscounts) ? bookedTariff.discountedPrice : bookedTariff.price) : 0
  );

/**
 * Get all booked tariff types
 * @description Get all booked tariff types by specified ticket type id
 * @param ticketTypeId
 * @returns Booked tariff types or empty array
 */
export const getAllBookedTariffTypesByTicketTypeId = (ticketTypeId: number) =>
  createSelector(
    getBookedTariffTypeByTicketTypeId(ticketTypeId),
    getBookedPackageTariffTypesByTicketTypeId(ticketTypeId),
    (bookedTariffType, bookedPackageTariffTypes): BookingTariffType[] => [bookedTariffType, ...bookedPackageTariffTypes]
  );

/**
 * Get total count from all booked products
 * @description Get booked products count - both single tariffs and also tariffs from packages with each single count
 * @returns Number which represents total count of all booked products
 */
export const getAllBookedProductsCount = createSelector(
  getAllBookedTariffs,
  (allBookedTariffs: BookingTariff[]): number =>
    allBookedTariffs.reduce((totalCount, bookedTariff) => totalCount + bookedTariff.count, 0)
);

/**
 * Is product count valid
 * @description Check if product is added to product booking list
 * @return Boolean
 */
export const isBookedProductsCountValid = createSelector(
  getAllBookedProductsCount,
  (allBookedProductsCount) => !!allBookedProductsCount
);

/**
 * Get sorted basket products
 * @description Sorts basket products to have packages, tariff types and parking tariff types consecutively. Tariff type tariffs that have discount are first in order.
 * @returns Array of sorted products
 */
export const getSortedBasketProducts = createSelector(
  getBookedProducts,
  (bookedProductTypes: BookedProducts): BookedProducts =>
    bookedProductTypes.sort(
      (currentBookedProductType: BookingProductType, nextBookedProductType: BookingProductType) => {
        if (isBookingProductTypePackage(currentBookedProductType)) {
          if (isBookingProductTypePackage(nextBookedProductType)) {
            return 0;
          }

          return -1;
        }

        if (isBookingProductTypeTariff(currentBookedProductType)) {
          if (isBookingProductTypePackage(nextBookedProductType)) {
            return 1;
          }

          if (isBookingProductTypeTariff(nextBookedProductType)) {
            const hasCurrentBookedTariffTypeAmanoParkingReservations = currentBookedProductType.tariffs.some(
              (bookedTariff) => !isEmpty(bookedTariff.amanoParkingReservations)
            );

            const hasNextBookedTariffTypeAmanoParkingReservations = nextBookedProductType.tariffs.some(
              (bookedTariff) => !isEmpty(bookedTariff.amanoParkingReservations)
            );

            if (
              !hasCurrentBookedTariffTypeAmanoParkingReservations &&
              !hasNextBookedTariffTypeAmanoParkingReservations
            ) {
              return 0;
            }

            if (
              hasCurrentBookedTariffTypeAmanoParkingReservations &&
              !hasNextBookedTariffTypeAmanoParkingReservations
            ) {
              return 1;
            }

            if (
              !hasCurrentBookedTariffTypeAmanoParkingReservations &&
              hasNextBookedTariffTypeAmanoParkingReservations
            ) {
              return -1;
            }
          }
        }
      }
    )
);

/**
 * Get total price from all booked products
 * @description Get booked products price - both single tariffs and also tariffs from packages
 * @returns Number which represents total price amount of all booked products
 */
export const getAllBookedProductsTotalPrice = createSelector(
  getAllBookedTariffs,
  selectRedeemedValidDiscountPromoCodes,
  (
    allProductsTotalPrice: BookingTariff[],
    redeemedValidDiscountPromoCodes: RedeemedPromoCode[]
  ): AllBookedProductsTotalPrice =>
    allProductsTotalPrice.reduce(
      ({ totalPrice, totalDiscountedPrice }, bookedTariff) => {
        const { price, discountedPrice, count, workshops } = bookedTariff;
        const bookedTariffDiscountedPrice = !isEmpty(redeemedValidDiscountPromoCodes) ? discountedPrice : price;

        if (!isEmpty(bookedTariff.amanoParkingReservations)) {
          return {
            totalPrice: totalPrice + price,
            totalDiscountedPrice: totalDiscountedPrice + bookedTariffDiscountedPrice
          };
        }

        workshops.forEach(({ tariffUuids, price: workshopPrice }) => {
          if (workshopPrice > 0) {
            const totalWorkshopPrice = tariffUuids.length * workshopPrice;

            totalPrice += totalWorkshopPrice;
            totalDiscountedPrice += totalWorkshopPrice;
          }
        });

        return {
          totalPrice: totalPrice + price * count,
          totalDiscountedPrice: totalDiscountedPrice + bookedTariffDiscountedPrice * count
        };
      },
      { totalPrice: 0, totalDiscountedPrice: 0 }
    )
);

/**
 * Get unique bookings with total cunt
 * @description Get unique bookings with accumulated count
 * @return Booked tariff or empty array
 */
export const getUniqueBookedTariffsWithTotalCount = createSelector(
  getAllBookedTariffs,
  (bookedTariffs: BookingTariff[]): BookingTariff[] => {
    return bookedTariffs.reduce((reducedTariffs: BookingTariff[], bookedTariff) => {
      const reducedTariff = reducedTariffs.find(
        (reducedTariff) => reducedTariff.ticketPersonId === bookedTariff.ticketPersonId
      );

      if (reducedTariff) {
        reducedTariff.count += bookedTariff.count;
        reducedTariff.tariffUuids = [...reducedTariff.tariffUuids, ...bookedTariff.tariffUuids];
      } else {
        reducedTariffs.push(bookedTariff);
      }

      return reducedTariffs;
    }, []);
  }
);

/**
 * Get unique bookings for release
 * @description Get unique bookings with count set to 0
 * @return Booked tariff or empty array
 */
export const getUniqueBookedTariffsForRelease = createSelector(
  getAllBookedTariffs,
  (bookedTariffs: BookingTariff[]): BookingTariff[] => {
    return bookedTariffs.reduce((reducedTariffs: BookingTariff[], bookedTariff) => {
      const reducedTariff = reducedTariffs.find(
        (reducedTariff) => reducedTariff.ticketPersonId === bookedTariff.ticketPersonId
      );

      if (reducedTariff) {
        reducedTariff.count = 0;
        reducedTariff.tariffUuids = [...reducedTariff.tariffUuids, ...bookedTariff.tariffUuids];
      } else {
        bookedTariff.count = 0;
        reducedTariffs.push(bookedTariff);
      }

      return reducedTariffs;
    }, []);
  }
);

/**
 * Get shared booked ticketPersonId tariffs total count
 * @description Get shared booked ticketPersonId tariffs total count sorted by ticketPersonId (current added tariff(s) count not included)
 * @returns Shared booked tariff count object sorted by ticketPersonId
 */
export const getSharedBookedPersonIdTariffsTotalCount = (bookingProductTypes: BookingProductType[]) =>
  createSelector(getAllBookedTariffs, (bookedTariffs: BookingTariff[]): SharedBookedPersonIdTariffsTotalCount => {
    const bookedTariffsCount: SharedBookedPersonIdTariffsTotalCount = {};

    bookedTariffs.forEach((bookedTariff) => {
      if (bookedTariffsCount[bookedTariff.ticketPersonId]) {
        bookedTariffsCount[bookedTariff.ticketPersonId] += bookedTariff.count;
      } else {
        bookedTariffsCount[bookedTariff.ticketPersonId] = bookedTariff.count;
      }
    });

    const decreaseBookingPersonIdTariffCount = (bookingTariff: BookingTariff) => {
      const bookedTariff = bookedTariffs.find(
        (bookedTariff) =>
          bookedTariff.ticketPersonId === bookingTariff.ticketPersonId &&
          bookedTariff.voucherCode === bookingTariff.voucherCode &&
          bookedTariff.packageNumber == bookingTariff.packageNumber &&
          bookedTariff.packageIndex === bookingTariff.packageIndex
      );

      if (bookedTariff) {
        bookedTariffsCount[bookedTariff.ticketPersonId] -= bookedTariff.count;
      }
    };

    bookingProductTypes.forEach((bookingProductType) => {
      if (isBookingProductTypeTariff(bookingProductType)) {
        bookingProductType.tariffs.forEach(decreaseBookingPersonIdTariffCount);
      } else if (isBookingProductTypePackage(bookingProductType)) {
        bookingProductType.productTypes.forEach((bookingPackageProductType) => {
          if (isBookingProductTypeTariff(bookingPackageProductType)) {
            bookingPackageProductType.tariffs.forEach(decreaseBookingPersonIdTariffCount);
          }
        });
      }
    });

    return bookedTariffsCount;
  });
